import React from "react";
import { useSelector } from "react-redux";

import { subMonths } from "~/tools/date-time";
import { isAnyErrorStatus, isAnyLoadingStatus, isIdleRequestStatus } from "~/tools/request-status";

import { selectTimestampDate } from "../../../../../app/state/view/timeline/TimelineSelectors";
import { LAST_6_MONTHS, SpecificRange } from "../../../../../app/state/view/timeline/TimeRange";
import type { TopologyEvent } from "../../../../../common/openapi";
import { jsonToYaml } from "../../../../../generic/yamlUtils";
import { useQueryTopologyEventById } from "../../../../../queries";
import { usePollingEventsHook } from "../../../panes/east/sections/events/usePollingEventsHook";

export interface LastChangeDiff {
	old: string;
	new: string;
	oldTimestamp: number;
	newTimestamp: number;
}

const selectLastChangeDiff = ({ data }: TopologyEvent): LastChangeDiff => ({
	old: jsonToYaml(data.old),
	new: jsonToYaml(data.new),
	oldTimestamp: data.oldTimestamp as number,
	newTimestamp: data.newTimestamp as number,
});

export const useQueryLastComponentChange = (
	componentId: number,
): {
	isLoading: boolean;
	error: null | Error;
	diff: null | LastChangeDiff;
} => {
	const timestamp = useSelector(selectTimestampDate);
	const lastSixMonthFromTimestamp = React.useMemo(
		() => (timestamp ? new SpecificRange(subMonths(timestamp, 6), timestamp) : LAST_6_MONTHS),
		[timestamp],
	);

	const { status, data: events } = usePollingEventsHook({
		topologyQuery: `id = "${componentId}"`,
		eventType: "Configuration Changed",
		// it needs the latest Configuration Changed event to show the diff
		eventsLimit: 1,
		eventsTimeRange: lastSixMonthFromTimestamp,
	});

	const isLoading = isAnyLoadingStatus(status) || isIdleRequestStatus(status);
	const isError = isAnyErrorStatus(status);
	const event = events.at(0);

	const result = useQueryTopologyEventById(
		{
			// it is ok to assign none sense values here, because the query is disabled when event is not defined
			eventId: event?.identifier ?? "",
			eventTime: event?.eventTime ?? 0,
		},
		{
			enabled: event != null,
			select: selectLastChangeDiff,
		},
	);

	return {
		isLoading: isLoading || result.isLoading,
		error: isError ? new Error("Pooling events error") : result.error,
		diff: result.data ?? null,
	};
};
